import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatDate } from '../utils/dateFormat';
import './ProfileEditHistory.css';

const FIELD_LABELS = {
  displayName: 'Display name',
  skillLevel: 'Skill level',
  yearsOfExperience: 'Years of experience'
};

function formatValue(field, value) {
  if (value === undefined || value === null || value === '') return '—';
  if (field === 'skillLevel') {
    const s = String(value).toLowerCase();
    return s === 'beginner' ? 'Beginner' : s === 'advanced' ? 'Advanced' : 'Average';
  }
  return String(value);
}

export default function ProfileEditHistory() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'profileEditLogs'),
          where('userId', '==', currentUser.uid),
          orderBy('editedAt', 'desc')
        );
        const snapshot = await getDocs(q);
        if (cancelled) return;
        setLogs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching profile edit logs:', err);
        if (!cancelled) setLogs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  return (
    <div className="profile-edit-history-container">
      <div className="profile-edit-history-content">
        <div className="profile-edit-history-header">
          <h1>Profile edit history</h1>
          <p className="profile-edit-history-intro">
            Changes made to your profile by admins. You can see what was changed and who made the edit.
          </p>
          <button onClick={() => navigate('/dashboard')} className="btn-secondary">
            Back to Dashboard
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="profile-edit-history-empty">
            <p>No one has edited your profile yet.</p>
            <p className="profile-edit-history-empty-hint">When an admin updates your display name, skill level, or years of experience, those edits will appear here.</p>
          </div>
        ) : (
          <ul className="profile-edit-history-list">
            {logs.map((log) => (
              <li key={log.id} className="profile-edit-history-card">
                <div className="profile-edit-history-meta">
                  <span className="profile-edit-history-date">
                    {log.editedAt ? formatDate(log.editedAt) : '—'}
                  </span>
                  <span className="profile-edit-history-by">
                    Edited by <strong>{log.editedByName || 'Unknown'}</strong>
                  </span>
                </div>
                <div className="profile-edit-history-changes">
                  {log.changes && typeof log.changes === 'object' && Object.entries(log.changes).map(([field, diff]) => (
                    <div key={field} className="profile-edit-history-change">
                      <span className="profile-edit-history-field">
                        {FIELD_LABELS[field] || field}:
                      </span>
                      <span className="profile-edit-history-diff">
                        &ldquo;{formatValue(field, diff.before)}&rdquo; → &ldquo;{formatValue(field, diff.after)}&rdquo;
                      </span>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
